import type { ReactNode } from 'react';
import { EditOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { Badge, Button, Dialog } from '../../../components/ui';
import { useT } from '../../../i18n';
import { BO_CUC_ND30, NHAN_KIEU_TRUONG } from '../constants';
import { xemTruocMau } from '../nghiep-vu/khung-van-ban';
import type { MauVanBan } from '../types';

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="px-3.5 py-2.5">
      <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-content-muted">{nhan}</div>
      <div className="text-sm text-content-primary">{children}</div>
    </div>
  );
}

function SoTron({ n }: { n: number }) {
  return (
    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-2xs font-semibold text-primary-700">
      {n}
    </span>
  );
}

/** Xem mẫu văn bản (hộp thoại "Xem mẫu" của bản cũ): thông tin, trường, bố cục NĐ30 và bản xem trước. */
export function XemMauDialog({
  mau,
  coAi,
  dangTao,
  coTheSua,
  coTheTao,
  onClose,
  onSua,
  onTaoXemTruoc,
  onSoanThao,
}: {
  mau: MauVanBan | null;
  /** Bản cài đã cấu hình LLM. */
  coAi: boolean;
  dangTao: boolean;
  coTheSua: boolean;
  /** Có quyền ghi `preview_content`. */
  coTheTao: boolean;
  onClose: () => void;
  onSua: (m: MauVanBan) => void;
  onTaoXemTruoc: (m: MauVanBan) => void;
  onSoanThao: (m: MauVanBan) => void;
}) {
  const { t } = useT();
  if (!mau) return null;
  const nd30 = mau.source === 'NGHI_DINH_30';
  const coThan = !nd30 && !!mau.body_template;
  const fields = mau.fields ?? [];

  const tieuDeXemTruoc = coThan
    ? t('vanBanMau.xemTruocNoiDung')
    : mau.preview_content
      ? t('vanBanMau.xemTruocAi')
      : mau.original_filename
        ? t('vanBanMau.xemTruocTep', { tep: mau.original_filename })
        : t('vanBanMau.xemTruocNoiDung');

  return (
    <Dialog
      open
      onClose={onClose}
      width={1100}
      destroyOnHidden
      title={t('vanBanMau.xemTieuDe', { ten: mau.name })}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('vanBanChung.dong')}
          </Button>
          {coTheSua && (
            <Button variant="outline" icon={<EditOutlined />} onClick={() => onSua(mau)}>
              {t('vanBanMau.sua')}
            </Button>
          )}
          <Button variant="primary" icon={<ThunderboltOutlined />} onClick={() => onSoanThao(mau)}>
            {t('vanBanMau.dungDeSoan')}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-[300px_1fr]">
        <div className="space-y-4">
          <div className="divide-y divide-default overflow-hidden rounded-lg border border-default bg-surface">
            <Dong nhan={t('vanBanMau.tTenMau')}>
              <span className="font-semibold">{mau.name}</span>
            </Dong>
            <Dong nhan={t('vanBanMau.tKyHieu')}>
              <Badge variant="neutral">{t('vanBanMau.kyHieuMau', { ma: mau.symbol_code })}</Badge>
            </Dong>
            <Dong nhan={t('vanBanMau.tNguon')}>
              <Badge variant={nd30 ? 'info' : 'orange'}>
                {nd30 ? t('vanBanMau.nguonNd30') : t('vanBanMau.nguonTuyChinh')}
              </Badge>
            </Dong>
            <Dong nhan={t('vanBanMau.tMoTa')}>
              <span className="leading-relaxed text-content-secondary">{mau.description || t('chung.khongCo')}</span>
            </Dong>
          </div>

          {fields.length > 0 && (
            <div>
              <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-content-secondary">
                {t('vanBanMau.cacTruong', { so: fields.length })}
              </div>
              <ul className="space-y-1.5">
                {fields.map((f, i) => (
                  <li key={f.key} className="flex items-start gap-2.5 text-sm text-content-secondary">
                    <SoTron n={i + 1} />
                    <span className="leading-snug">
                      <b className="text-content-primary">{f.label}</b>
                      {f.required && <span className="text-danger-600"> *</span>}
                      <span className="ml-1 text-2xs text-content-muted">({t(NHAN_KIEU_TRUONG[f.type])})</span>
                      {f.ai_fill !== false && <span className="ml-1 text-2xs text-violet-600">{t('vanBanMau.aiDien')}</span>}
                      {f.hint && <span className="block text-xs text-content-muted">{f.hint}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="mb-2 text-2xs font-semibold uppercase tracking-wide text-content-secondary">
              {t('vanBanMau.boCucTieuDe')}{' '}
              <span className="font-normal normal-case text-content-muted">{t('vanBanMau.boCucNguon')}</span>
            </div>
            <ol className="space-y-1.5">
              {BO_CUC_ND30.map((k, i) => (
                <li key={k} className="flex items-start gap-2.5 text-sm text-content-secondary">
                  <SoTron n={i + 1} />
                  <span className="leading-snug">{t(k)}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-default">
          <div className="flex items-center justify-between gap-2 border-b border-default bg-subtle px-3.5 py-2.5 text-xs font-semibold text-content-primary">
            <span className="flex items-center gap-1.5">
              <EyeOutlined className="text-primary-500" />
              {tieuDeXemTruoc}
            </span>
            {/* Mẫu tuỳ chỉnh đã có thân; chỉ mẫu NĐ30 cần AI sinh minh hoạ. */}
            {nd30 && coAi && coTheTao && (
              <Button
                variant="outline"
                size="sm"
                className="ai-glow"
                icon={<Sparkles size={14} className="ai-sparkle" />}
                loading={dangTao}
                onClick={() => onTaoXemTruoc(mau)}
              >
                {dangTao
                  ? t('vanBanMau.dangTaoXemTruoc')
                  : mau.preview_content
                    ? t('vanBanMau.taoLai')
                    : t('vanBanMau.taoXemTruoc')}
              </Button>
            )}
          </div>
          <div className="max-h-[56vh] overflow-y-auto bg-page p-4">
            {coThan ? (
              <div
                className="mvb-doc mx-auto max-w-[720px] rounded-lg shadow-sm"
                // Thân đã lọc HTML theo danh sách cho phép ở backend (HtmlLoc) trước khi lưu
                dangerouslySetInnerHTML={{
                  __html: xemTruocMau(mau.body_template, mau.fields, mau.default_meta, mau.name, mau.symbol_code),
                }}
              />
            ) : mau.preview_content ? (
              <div className="whitespace-pre-wrap rounded-lg bg-surface p-4 text-sm leading-relaxed text-content-secondary">
                {mau.preview_content}
              </div>
            ) : mau.template_content ? (
              <div className="whitespace-pre-wrap rounded-lg bg-surface p-4 text-sm leading-relaxed text-content-secondary">
                {mau.template_content}
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-content-muted">
                <ThunderboltOutlined className="mb-2.5 text-2xl" />
                <p>{t(coAi ? 'vanBanMau.chuaCoNoiDung' : 'vanBanMau.chuaCoNoiDungKhongAi')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
